import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import apiUrls from "../../../api";
import type { RootState } from "../../../common/state/store";
import type { IAppLoaderAction } from "../../../common/state/loaderHandleMiddleware";
import type { ISession } from "../../../model/model";
import graphColors from "../../../assets/graphColors";

export interface ISessionsByProject {
  project_name: string;
  project_id: number;
  need_login: boolean;
  is_simplified_login: boolean;
  sessions: ISession[];
  is_deleted: boolean;
}

export interface ISessionsByProjectGroup {
  project_group_name: string;
  project_group_id: number;
  projects: ISessionsByProjectWithColor[];
  need_login: boolean;
  is_simplified_login: boolean;
  sessions: ISession[];
  total_projectGroups: number;
  is_deleted: boolean;
}

export interface ISessionsByProjectWithColor extends ISessionsByProject {
  color: string;
}

export interface ISessionsByProjectGroupWithColor extends ISessionsByProjectGroup {
  color: string;
}

export interface ScorePerScene {
  id: string | number;
  name: string;
  score: number;
  score_max: number;
}

export interface ScorePerProjectGroup {
  project_group_id: number;
  projects: ScorePerScene[];
}

export interface ICurrentProjectGroupElement {
  id: string | number;
  order: number;
  project: {
    id: string | number;
    name: string;
  };
  project_group: {
    id: string | number;
    auth_type: string;
    name: string;
  };
}

export interface IStatsState {
  allSessionsByProject: ISessionsByProjectWithColor[];
  allSessionsByProjectGroup: ISessionsByProjectGroup[];
  hasLoadedSessionsByProjectGroup: boolean;
  selectedProjectId: number;
  selectedProjectGroupId: number;
  selectedProjectScorePerScene: ScorePerScene[];
  selectedProjectGroupScorePerProject: ScorePerProjectGroup;
  selectedUserProjectScore: IEnduserStatsPerProject;
  currentProjectGroupElement: ICurrentProjectGroupElement;
  // URL of the getAllSessionsByProjectGroup request currently in flight, if
  // any; used by the thunk's `condition` to skip an identical dispatch fired
  // while the first one hasn't resolved yet (login-fanout diet, Phase 4).
  inFlightSessionsByProjectGroupUrl: string | null;
  // requestId of the most recently *issued* getAllSessionsByProjectGroup
  // thunk; used by the .fulfilled/.rejected guards below so an older request
  // settling after a newer one has started doesn't clear inFlightSessionsByProjectGroupUrl
  // out from under the newer (still in-flight) request - which would let a
  // duplicate dispatch for that newer request's URL sneak past the `condition`
  // guard above. Mirrors projectGroupsSlice's latestListRequestId.
  latestSessionsByProjectGroupRequestId: string | null;
}

export interface IEnduserStatsPerProject {
  enduser: number;
  first_session: string;
  last_session: string;
  project: string;
  user_name: string;
  first_session_score: number;
  last_session_score: number;
  project_max_score: number;
  firstSessionScoreAndMaxScoreString: string;
  lastSessionScoreAndMaxScoreString: string;
  sourceAvatarId: number;
  email: string;
  lastSessionCompleted: boolean;
  rank: number;
  competitors: number;
  successRate: ScorePerScene[];
}

const initialState: IStatsState = {
  allSessionsByProject: [],
  allSessionsByProjectGroup: [],
  hasLoadedSessionsByProjectGroup: false,
  inFlightSessionsByProjectGroupUrl: null,
  latestSessionsByProjectGroupRequestId: null,
  selectedProjectId: 0,
  selectedProjectGroupId: 0,
  selectedProjectScorePerScene: [],
  selectedProjectGroupScorePerProject: {
    project_group_id: 0,
    projects: [],
  },
  selectedUserProjectScore: {
    enduser: 0,
    first_session: "",
    last_session: "",
    project: "0",
    user_name: "",
    first_session_score: 0,
    last_session_score: 0,
    project_max_score: 0,
    firstSessionScoreAndMaxScoreString: "",
    lastSessionScoreAndMaxScoreString: "",
    sourceAvatarId: 0,
    email: "",
    lastSessionCompleted: true,
    rank: 0,
    competitors: 0,
    successRate: [],
  },
  currentProjectGroupElement: {
    id: 0,
    order: 0,
    project: { id: 0, name: "" },
    project_group: { id: 0, auth_type: "", name: "" },
  },
};

export const getAllSessionsByProject = createAsyncThunk(
  "stats/allSessionsByProject",
  // eslint-disable-next-line no-empty-pattern
  async ({}: IAppLoaderAction) => {
    const response = await axios.get<ISessionsByProject[]>(
      `${apiUrls.stats.getAllSessionsByProject}`,
    );
    return { data: response.data };
  },
);

const buildSessionsByProjectGroupUrl = (mode?: "summary"): string =>
  mode === "summary"
    ? `${apiUrls.stats.getAllSessionsByProjectGroup}?mode=summary`
    : `${apiUrls.stats.getAllSessionsByProjectGroup}`;

export const getAllSessionsByProjectGroup = createAsyncThunk(
  "stats/allSessionsByProjectGroup",
  async ({ mode }: { mode?: "summary"; force?: boolean } & IAppLoaderAction) => {
    // "summary" = date-only sessions for the dashboard charts (backend Phase 3);
    // the StatisticsNew views keep the full shape.
    const url = buildSessionsByProjectGroupUrl(mode);
    const response = await axios.get<ISessionsByProjectGroup[]>(url);
    return { data: response.data };
  },
  {
    // Skip a second identical dispatch fired while the first is still in
    // flight (login-fanout diet, Phase 4): StatsOverview's mount effect can
    // fire this twice (e.g. React StrictMode's dev-only double-invoke).
    condition: ({ mode, force }, { getState }) =>
      force === true ||
      (getState() as RootState).stats.inFlightSessionsByProjectGroupUrl !==
        buildSessionsByProjectGroupUrl(mode),
  },
);

export const fetchCurrentProjectGroupElement = createAsyncThunk(
  "stats/fetchCurrentProjectGroupElement",

  async ({ projectId }: { projectId: number } & IAppLoaderAction) => {
    const response = await axios.get<ICurrentProjectGroupElement[]>(
      `${apiUrls.projects.projectGroupElements}?filters[project][id][$eq]=${projectId}&populate=project,project_group`,
    );
    const cpge = response.data[0];
    return cpge;
  },
);

export const getScorePerSceneForProject = createAsyncThunk(
  "stats/scorePerSceneForProject",

  async ({
    projectId,
    startDate,
    endDate,
  }: { projectId: number; startDate?: string; endDate?: string } & IAppLoaderAction) => {
    let url = `${apiUrls.stats.successRatePerScene}?project=${projectId}`;
    if (startDate) {
      url += `&startDate=${startDate}`;
    }
    if (endDate) {
      url += `&endDate=${endDate}`;
    }
    const response = await axios.get<ScorePerScene[]>(url);
    return response.data;
  },
);

export const getAvarageScorePerProjectForProjectGroup = createAsyncThunk(
  "stats/getAvarageScorePerProjectForProjectGroup",

  async ({ projectGroupId }: { projectGroupId: number } & IAppLoaderAction) => {
    const response = await axios.get<ScorePerProjectGroup>(
      `${apiUrls.stats.successRatePerProjectGroup}?projectGroupId=${projectGroupId}`,
    );
    return response.data;
  },
);

export const getEnduserStatsPerProject = createAsyncThunk(
  "stats/enduserStatsPerProject",

  async ({ userId, projectId }: { userId?: number; projectId: number } & IAppLoaderAction) => {
    const response = await axios.get<IEnduserStatsPerProject>(
      `${apiUrls.stats.enduserStatsPerProject}?user=${userId}&project=${projectId}`,
    );
    return response.data;
  },
);

export const sendProjectResultsPerUser = createAsyncThunk(
  "stats/sendProjectResultsPerUser",
  async ({
    projectId,
    trainerId,
    traineeId,
    to,
  }: {
    projectId: number;
    trainerId: number;
    traineeId: number;
    to: string;
  } & IAppLoaderAction) => {
    const response = await axios.post<unknown>(
      `${apiUrls.stats.sendProjectResults}?projectId=${projectId}&trainerId=${trainerId}&traineeId=${traineeId}&to=${to}`,
    );
    return response.data;
  },
);

export const sendProjectResultsGlobal = createAsyncThunk(
  "stats/sendProjectResultsGlobal",
  async ({ projectId, trainerId }: { projectId: number; trainerId: number } & IAppLoaderAction) => {
    const response = await axios.get<unknown>(
      `${apiUrls.stats.sendGlobalResults}?projectId=${projectId}&trainerId=${trainerId}`,
    );
    return response.data;
  },
);

export const fetchGlobalResultsDownloadLink = createAsyncThunk(
  "stats/fetchGlobalResultsDownloadLink",
  async ({ projectId }: { projectId: number } & IAppLoaderAction) => {
    const response = await axios.get<{ s3_url: string }>(
      `${apiUrls.stats.getGlobalResultsDownloadLink}?projectId=${projectId}`,
    );
    return response.data;
  },
);

export const statsSlice = createSlice({
  name: "stats",
  initialState,
  extraReducers: (builder) => {
    builder.addCase(getAllSessionsByProject.fulfilled, (state: IStatsState, { payload }) => {
      const projects = payload.data;

      state.allSessionsByProject = projects.map((project: ISessionsByProject, index: number) => {
        return {
          ...project,
          color: graphColors[index % graphColors.length],
        };
      });
    });
    builder.addCase(getAllSessionsByProjectGroup.pending, (state: IStatsState, action) => {
      state.latestSessionsByProjectGroupRequestId = action.meta.requestId;
      state.inFlightSessionsByProjectGroupUrl = buildSessionsByProjectGroupUrl(
        action.meta.arg.mode,
      );
    });
    builder.addCase(getAllSessionsByProjectGroup.fulfilled, (state: IStatsState, action) => {
      const { payload } = action;
      const groups = payload.data;
      state.hasLoadedSessionsByProjectGroup = true;
      // Only clear the in-flight marker if this response belongs to the most
      // recently issued request - otherwise a stale request resolving after a
      // newer one has started would wipe the newer request's in-flight marker
      // and let a duplicate dispatch for it slip past the `condition` guard.
      if (action.meta.requestId === state.latestSessionsByProjectGroupRequestId) {
        state.inFlightSessionsByProjectGroupUrl = null;
      }
      state.allSessionsByProjectGroup = groups.map(
        (group: ISessionsByProjectGroup, index: number) => {
          return {
            ...group,
            projects: (group.projects ?? []).map(
              (project: ISessionsByProjectWithColor, index: number) => {
                return {
                  ...project,
                  color: graphColors[index % graphColors.length],
                };
              },
            ),
            color: graphColors[index % graphColors.length],
          };
        },
      );
    });
    builder.addCase(getAllSessionsByProjectGroup.rejected, (state: IStatsState, action) => {
      if (action.meta.requestId === state.latestSessionsByProjectGroupRequestId) {
        state.inFlightSessionsByProjectGroupUrl = null;
      }
    });
    builder.addCase(getScorePerSceneForProject.fulfilled, (state: IStatsState, { payload }) => {
      state.selectedProjectScorePerScene = payload;
    });
    builder.addCase(
      getAvarageScorePerProjectForProjectGroup.fulfilled,
      (state: IStatsState, { payload }) => {
        state.selectedProjectGroupScorePerProject = payload;
      },
    );
    builder.addCase(getEnduserStatsPerProject.fulfilled, (state: IStatsState, { payload }) => {
      state.selectedUserProjectScore = payload;
    });
    builder.addCase(
      fetchCurrentProjectGroupElement.fulfilled,
      (state: IStatsState, { payload }) => {
        if (payload) {
          state.currentProjectGroupElement = payload;
        }
      },
    );
  },
  reducers: {
    setCurrentSessions: (
      state: IStatsState,
      action: PayloadAction<ISessionsByProjectWithColor[]>,
    ) => {
      state.allSessionsByProject = action.payload;
    },
    setSelectedProjectId: (state: IStatsState, action: PayloadAction<number>) => {
      state.selectedProjectId = action.payload;
    },
    setSelectedProjectGroupId: (state: IStatsState, action: PayloadAction<number>) => {
      state.selectedProjectGroupId = action.payload;
    },
    setSelectedProjectScorePerScene: (
      state: IStatsState,
      action: PayloadAction<ScorePerScene[]>,
    ) => {
      state.selectedProjectScorePerScene = action.payload;
    },
    setSelectedProjectGroupScorePerProject: (
      state: IStatsState,
      action: PayloadAction<ScorePerProjectGroup>,
    ) => {
      state.selectedProjectGroupScorePerProject = action.payload;
    },
  },
});

export const sessionsByProjectGroupSelector = (state: RootState) =>
  state.stats.allSessionsByProjectGroup;

export const selectHasDemoSessions = createSelector(
  sessionsByProjectGroupSelector,
  (groups: ISessionsByProjectGroup[]) =>
    groups.some((group) => group.sessions?.some((session) => session.is_demo === true)),
);

export const hasLoadedSessionsByProjectGroupSelector = (state: RootState) =>
  state.stats.hasLoadedSessionsByProjectGroup;

// https://stackoverflow.com/a/62546164
export const getSelectedProjectWithSessions = (projectId: number) => (state: RootState) => {
  for (const group of state.stats.allSessionsByProjectGroup) {
    const project = group.projects.find((p) => Number(p.project_id) === Number(projectId));

    // Return as soon as a project is found
    if (project) {
      return project;
    }
  }

  // If no project is found after all groups have been checked, return a default empty project
  return {} as ISessionsByProjectWithColor;
};

export const getSelectedProjectGroupWithSessions =
  (projectGroupId: number) => (state: RootState) => {
    return (
      state.stats.allSessionsByProjectGroup.find(
        (sbp) => Number(sbp.project_group_id) === Number(projectGroupId),
      ) || ({} as ISessionsByProjectGroup)
    );
  };

export const getSelectedProjectId = (state: RootState) => state.stats.selectedProjectId;
export const getSelectedProjectGroupId = (state: RootState) => state.stats.selectedProjectGroupId;

export const getSelectedProjectScorePerScene = (state: RootState) =>
  state.stats.selectedProjectScorePerScene;

export const getSelectedProjectGroupScorePerProject = (state: RootState) =>
  state.stats.selectedProjectGroupScorePerProject;

export const getSelectedUserProjectScore = (state: RootState) =>
  state.stats.selectedUserProjectScore;

export const getCurrentProjectGroupElement = (state: RootState) =>
  state.stats.currentProjectGroupElement;

export const statsReducer = statsSlice.reducer;

export const {
  setCurrentSessions,
  setSelectedProjectGroupId,
  setSelectedProjectId,
  setSelectedProjectScorePerScene,
} = statsSlice.actions;
